import axiosInstance from '../../../api/axiosConfig';

// ============================================
// TASK FETCHING
// ============================================

export const fetchTasks = async (endpoint, params) => {
    const response = await axiosInstance.get(endpoint, { params });
    return response.data;
};

export const fetchGroupedTasks = async (params) => {
    const response = await axiosInstance.get('/api/tasks/grouped', { params });
    return response.data;
};

export const fetchTaskDetails = async (taskId) => {
    const response = await axiosInstance.get(`/api/tasks/${taskId}`);
    return response.data;
};

export const fetchSubtasks = async (parentId) => {
    const response = await axiosInstance.get(`/api/tasks/${parentId}/subtasks`);
    return response.data;
};

export const searchTasksForLinking = async (searchQuery, excludeTaskId, limit = 20) => {
    const response = await axiosInstance.get('/api/tasks/for-linking', {
        params: { search: searchQuery, exclude: excludeTaskId, limit }
    });
    return response.data;
};

// ============================================
// STATISTICS
// ============================================

export const fetchDashboardStats = async (view) => {
    const response = await axiosInstance.get('/api/tasks/dashboard-stats', {
        params: { view }
    });
    return response.data;
};

export const fetchManagerApprovalStats = async () => {
    const response = await axiosInstance.get('/api/tasks/manager-approval-stats');
    return response.data;
};

// ============================================
// USER MANAGEMENT
// ============================================

export const fetchUsers = async () => {
    const response = await axiosInstance.get('/api/tasks/users');
    return response.data;
};

// ============================================
// TASK CREATION & UPDATES
// ============================================

export const createTask = async (taskData, attachmentFiles = []) => {
    if (attachmentFiles.length > 0) {
        const formData = new FormData();
        Object.keys(taskData).forEach(key => {
            if (key === 'assignedTo') {
                formData.append(key, JSON.stringify(taskData[key]));
            } else if (taskData[key]) {
                formData.append(key, taskData[key]);
            }
        });
        attachmentFiles.forEach(file => formData.append('attachments', file));
        
        const response = await axiosInstance.post('/api/tasks', formData);
        return response.data;
    } else {
        const response = await axiosInstance.post('/api/tasks', taskData);
        return response.data;
    }
};

export const updateTask = async (taskId, updatedData) => {
    const response = await axiosInstance.put(`/api/tasks/${taskId}`, updatedData);
    return response.data;
};

export const updateTaskStatus = async (taskId, status) => {
    const response = await axiosInstance.patch(`/api/tasks/${taskId}/status`, { status });
    return response.data;
};

export const deleteTask = async (taskId) => {
    const response = await axiosInstance.delete(`/api/tasks/${taskId}`);
    return response.data;
};

// ============================================
// MICROSOFT TO DO SYNC
// ============================================

export const syncFromTodo = async () => {
    const response = await axiosInstance.post('/api/tasks/sync-todo');
    return response.data;
};

// ============================================
// SUBTASKS
// ============================================

export const createSubtask = async (parentTaskId, subtaskData) => {
    const response = await axiosInstance.post(
        `/api/tasks/${parentTaskId}/subtasks`,
        subtaskData
    );
    return response.data;
};

export const updateSubtaskStatus = async (subtaskId, status) => {
    const response = await axiosInstance.patch(
        `/api/tasks/${subtaskId}/status`, 
        { status }
    );
    return response.data;
};

export const updateSubtask = async (subtaskId, subtaskData) => {
    const response = await axiosInstance.put(
        `/api/tasks/${subtaskId}`,
        subtaskData
    );
    return response.data;
};

export const deleteSubtask = async (subtaskId) => {
    const response = await axiosInstance.delete(`/api/tasks/${subtaskId}`);
    return response.data;
};

// ============================================
// COMMENTS
// ============================================

export const addComment = async (taskId, comment) => {
    const response = await axiosInstance.post(
        `/api/tasks/${taskId}/comments`,
        { comment }
    );
    return response.data;
};

export const markCommentsAsRead = async (taskId) => {
    const response = await axiosInstance.patch(
        `/api/tasks/${taskId}/comments/read`,
        {}
    );
    return response.data;
};

// ============================================
// APPROVAL ACTIONS
// ============================================

export const approveTask = async (taskId, remarks = '') => {
    const payload = remarks ? { remarks } : {};
    const response = await axiosInstance.patch(`/api/tasks/${taskId}/approve`, payload);
    return response.data;
};

export const rejectTask = async (taskId, reason = '') => {
    const payload = reason ? { reason } : {};
    const response = await axiosInstance.patch(`/api/tasks/${taskId}/reject`, payload);
    return response.data;
};

// ============================================
// VERIFICATION ACTIONS
// ============================================

export const verifyTask = async (taskId, remarks = '') => {
    const payload = remarks ? { remarks } : {};
    const response = await axiosInstance.patch(`/api/tasks/${taskId}/verify`, payload);
    return response.data;
};

export const unverifyTask = async (taskId, reason = '') => {
    const payload = reason ? { reason } : {};
    const response = await axiosInstance.patch(`/api/tasks/${taskId}/unverify`, payload);
    return response.data;
};

// ============================================
// TASK CONNECTIONS
// ============================================

export const addTaskConnection = async (taskId, linkedTaskId, type) => {
    const response = await axiosInstance.post(
        `/api/tasks/${taskId}/connections`,
        { taskId: linkedTaskId, type }
    );
    return response.data;
};

export const removeTaskConnection = async (taskId, connectionId) => {
    const response = await axiosInstance.delete(
        `/api/tasks/${taskId}/connections/${connectionId}`
    );
    return response.data;
};

// ============================================
// REASSIGNMENT
// ============================================

export const reassignTask = async (taskId, assignedTo, reason = '') => {
    const response = await axiosInstance.patch(
        `/api/tasks/${taskId}/reassign`,
        { assignedTo, reason }
    );
    return response.data;
};
